// src/pages/Inventory/Inventory.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge, Pagination, InputGroup, Spinner } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBoxes, FaMinus, FaPlus as FaPlusSmall, FaStore, FaBox, FaSort, FaEye, FaArrowUp, FaArrowDown, FaExclamationTriangle, FaCheckCircle, FaUser, FaTag } from 'react-icons/fa';
import { inventoryAPI, storesAPI, vendorsAPI, productsAPI, variantsAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Inventory = () => {
    const [inventory, setInventory] = useState([]);
    const [filteredInventory, setFilteredInventory] = useState([]);
    const [stores, setStores] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [filteredVendors, setFilteredVendors] = useState([]); // Vendors for selected store
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]); // Products for selected vendor
    const [variants, setVariants] = useState([]);
    const [filteredVariants, setFilteredVariants] = useState([]); // Variants for selected product
    const [loading, setLoading] = useState(true);
    const [storesLoading, setStoresLoading] = useState(true);
    const [vendorsLoading, setVendorsLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    const [variantsLoading, setVariantsLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [selectedVendor, setSelectedVendor] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedVariant, setSelectedVariant] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'quantity', direction: 'desc' });

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 1
    });

    // Form states
    const [formData, setFormData] = useState({
        store_id: '',
        vendor_id: '',
        product_id: '',
        product_variant_id: '',
        quantity: '',
        min_stock_level: '10',
        reorder_point: '20'
    });

    const [adjustData, setAdjustData] = useState({
        quantity_delta: '',
        reason: '',
        notes: ''
    });

    // Fetch all data on component mount
    useEffect(() => {
        fetchStores();
        fetchAllVendors();
        fetchAllProducts();
        fetchAllVariants();
    }, []);

    // Fetch inventory when filters change
    useEffect(() => {
        fetchInventory();
    }, [pagination.page, selectedStore, selectedVariant]);

    // Filter vendors when store is selected in form
    useEffect(() => {
        if (formData.store_id && vendors.length > 0) {
            // Get vendors associated with this store
            const storeVendors = vendors.filter(vendor =>
                vendor.stores && vendor.stores.some(store => store.id === formData.store_id)
            );
            setFilteredVendors(storeVendors);

            // Reset vendor, product, variant when store changes
            setFormData(prev => ({
                ...prev,
                vendor_id: '',
                product_id: '',
                product_variant_id: ''
            }));
        } else {
            setFilteredVendors([]);
        }
    }, [formData.store_id, vendors]);

    // Filter products when vendor is selected in form
    useEffect(() => {
        if (formData.vendor_id && products.length > 0) {
            // Get products for this vendor
            const vendorProducts = products.filter(product =>
                product.vendor_id === formData.vendor_id
            );
            setFilteredProducts(vendorProducts);

            // Reset product and variant when vendor changes
            setFormData(prev => ({
                ...prev,
                product_id: '',
                product_variant_id: ''
            }));
        } else {
            setFilteredProducts([]);
        }
    }, [formData.vendor_id, products]);

    // Filter variants when product is selected in form
    useEffect(() => {
        if (formData.product_id && variants.length > 0) {
            // Get variants for this product
            const productVariants = variants.filter(variant =>
                variant.product_id === formData.product_id
            );
            setFilteredVariants(productVariants);

            // Reset variant when product changes
            setFormData(prev => ({
                ...prev,
                product_variant_id: ''
            }));
        } else {
            setFilteredVariants([]);
        }
    }, [formData.product_id, variants]);

    // Filter inventory based on search and filters
    useEffect(() => {
        if (Array.isArray(inventory)) {
            let filtered = inventory;

            // Apply search filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                filtered = filtered.filter(item =>
                    (item.product_variant_name || '').toLowerCase().includes(searchLower) ||
                    (item.store_name || '').toLowerCase().includes(searchLower) ||
                    (item.vendor_name || '').toLowerCase().includes(searchLower) ||
                    (item.product_name || '').toLowerCase().includes(searchLower) ||
                    (item.sku || '').toLowerCase().includes(searchLower)
                );
            }

            // Apply store filter
            if (selectedStore) {
                filtered = filtered.filter(item => item.store_id === selectedStore);
            }

            // Apply variant filter
            if (selectedVariant) {
                filtered = filtered.filter(item => item.product_variant_id === selectedVariant);
            }

            // Apply sorting
            if (sortConfig.key) {
                filtered.sort((a, b) => {
                    if (a[sortConfig.key] < b[sortConfig.key]) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (a[sortConfig.key] > b[sortConfig.key]) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                });
            }

            setFilteredInventory(filtered);
        }
    }, [searchTerm, selectedStore, selectedVariant, inventory, sortConfig]);

    // Fetch stores
    const fetchStores = async () => {
        try {
            setStoresLoading(true);
            const response = await storesAPI.list({ page: 1, limit: 100 });

            console.log('Stores API Response in Inventory:', response.data);

            let storesData = [];

            // Handle the stores API response structure
            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.stores && Array.isArray(response.data.data.stores)) {
                    storesData = response.data.data.stores;
                } else if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    storesData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    storesData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                storesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                storesData = response.data;
            } else if (response.data && response.data.stores && Array.isArray(response.data.stores)) {
                storesData = response.data.stores;
            }

            if (!Array.isArray(storesData)) {
                console.error('Invalid stores data format:', response.data);
                storesData = [];
            }

            // Process stores
            const processedStores = storesData.map(store => ({
                id: store.id,
                name: store.name || 'Unnamed Store',
                code: store.code || '',
                address: store.address || '',
                city: store.city || '',
                state: store.state || '',
                is_active: store.is_active !== undefined ? store.is_active : 1,
                status: store.status || (store.is_active === 1 ? 'active' : 'inactive')
            }));

            console.log('Processed stores for inventory:', processedStores.length);
            setStores(processedStores);
        } catch (err) {
            console.error('Error fetching stores:', err);
            setError('Failed to fetch stores');
        } finally {
            setStoresLoading(false);
        }
    };

    // Fetch all vendors
    const fetchAllVendors = async () => {
        try {
            const response = await vendorsAPI.list({ page: 1, limit: 100 });

            console.log('Vendors API Response:', response.data);

            let vendorsData = [];

            // Handle vendors API response structure
            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    vendorsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    vendorsData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                vendorsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                vendorsData = response.data;
            }

            if (!Array.isArray(vendorsData)) {
                console.error('Invalid vendors data format:', response.data);
                vendorsData = [];
            }

            // Process vendors
            const processedVendors = vendorsData.map(vendor => ({
                id: vendor.id,
                name: vendor.name || 'Unnamed Vendor',
                email: vendor.email || '',
                phone: vendor.phone || '',
                stores: vendor.stores || [],
                store_ids: vendor.store_ids || (vendor.stores ? vendor.stores.map(s => s.id) : [])
            }));

            console.log('Processed vendors:', processedVendors.length);
            setVendors(processedVendors);
        } catch (err) {
            console.error('Error fetching vendors:', err);
        }
    };

    // Fetch all products
    const fetchAllProducts = async () => {
        try {
            const response = await productsAPI.list({ page: 1, limit: 500 });

            console.log('Products API Response:', response.data);

            let productsData = [];

            // Handle products API response structure
            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    productsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    productsData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                productsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                productsData = response.data;
            }

            if (!Array.isArray(productsData)) {
                console.error('Invalid products data format:', response.data);
                productsData = [];
            }

            // Process products
            const processedProducts = productsData.map(product => ({
                id: product.id,
                name: product.name || 'Unnamed Product',
                sku: product.sku || '',
                vendor_id: product.vendor_id || '',
                category_id: product.category_id || '',
                subcategory_id: product.subcategory_id || '',
                is_active: product.is_active !== undefined ? product.is_active : 1
            }));

            console.log('Processed products:', processedProducts.length);
            setProducts(processedProducts);
        } catch (err) {
            console.error('Error fetching products:', err);
        }
    };

    // Fetch all variants
    const fetchAllVariants = async () => {
        try {
            const response = await variantsAPI.list({ page: 1, limit: 500 });

            console.log('Variants API Response:', response.data);

            let variantsData = [];

            // Handle variants API response structure
            if (response.data && response.data.status === 'success' && response.data.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    variantsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    variantsData = response.data.data;
                }
            } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
                variantsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                variantsData = response.data;
            }

            if (!Array.isArray(variantsData)) {
                console.error('Invalid variants data format:', response.data);
                variantsData = [];
            }

            // Process variants
            const processedVariants = variantsData.map(variant => ({
                id: variant.id,
                title: variant.title || variant.name || 'Unnamed Variant',
                name: variant.name || variant.title || 'Unnamed Variant',
                sku: variant.sku || 'N/A',
                product_id: variant.product_id || '',
                product_name: variant.product_name || 'Unknown Product',
                price: variant.price || 0,
                compare_price: variant.compare_price || 0,
                attributes: variant.attributes || {},
                is_active: variant.is_active !== undefined ? variant.is_active : 1
            }));

            console.log('Processed variants:', processedVariants.length);
            setVariants(processedVariants);
        } catch (err) {
            console.error('Error fetching variants:', err);
        }
    };

    // Fetch inventory with pagination
    const fetchInventory = async () => {
        try {
            setLoading(true);
            setError('');

            const params = {
                page: pagination.page,
                limit: pagination.limit
            };

            if (selectedStore) {
                params.store_id = selectedStore;
            }

            if (selectedVariant) {
                params.product_variant_id = selectedVariant;
            }

            const response = await inventoryAPI.list(params);

            console.log('Inventory API Response:', response.data);

            let inventoryData = [];
            let paginationData = {};

            if (response.data && response.data.status === 'success') {
                if (response.data.data && Array.isArray(response.data.data)) {
                    inventoryData = response.data.data;
                } else if (response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
                    inventoryData = response.data.data.data;
                    paginationData = response.data.data.meta || {};
                } else if (response.data.data && typeof response.data.data === 'object') {
                    inventoryData = [response.data.data];
                }
            } else if (response.data && Array.isArray(response.data)) {
                inventoryData = response.data;
            }

            if (!Array.isArray(inventoryData)) {
                console.error('Invalid inventory data format:', response.data);
                inventoryData = [];
            }

            // Enrich inventory data with store, vendor, product, and variant names
            const enrichedData = inventoryData.map(item => {
                const store = stores.find(s => s.id === item.store_id);
                const variant = variants.find(v => v.id === item.product_variant_id);
                const product = products.find(p => p.id === variant?.product_id);
                const vendor = vendors.find(v => v.id === product?.vendor_id);

                return {
                    ...item,
                    id: item.id || item.inventory_id,
                    store_name: store?.name || item.store_id,
                    store_code: store?.code || '',
                    vendor_name: vendor?.name || 'Unknown Vendor',
                    vendor_id: vendor?.id || '',
                    product_name: product?.name || variant?.product_name || 'Unknown Product',
                    product_id: product?.id || '',
                    product_variant_name: variant?.title || variant?.name || item.product_variant_id,
                    sku: variant?.sku || 'N/A',
                    variant_title: variant?.title || variant?.name || 'Unknown Variant'
                };
            });

            setInventory(enrichedData);
            setFilteredInventory(enrichedData);
            setPagination(prev => ({
                ...prev,
                ...paginationData,
                total: paginationData.total || enrichedData.length,
                pages: paginationData.pages || Math.ceil((paginationData.total || enrichedData.length) / prev.limit)
            }));

        } catch (err) {
            console.error('Error fetching inventory:', err);
            setError(err.response?.data?.message || 'Failed to fetch inventory');
            setInventory([]);
            setFilteredInventory([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle store change in form
    const handleStoreChange = (storeId) => {
        setFormData({
            ...formData,
            store_id: storeId,
            vendor_id: '',
            product_id: '',
            product_variant_id: ''
        });
    };

    // Handle vendor change in form
    const handleVendorChange = (vendorId) => {
        setFormData({
            ...formData,
            vendor_id: vendorId,
            product_id: '',
            product_variant_id: ''
        });
    };

    // Handle product change in form
    const handleProductChange = (productId) => {
        setFormData({
            ...formData,
            product_id: productId,
            product_variant_id: ''
        });
    };

    // Handle sorting
    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Create inventory item
    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.store_id || !formData.vendor_id || !formData.product_id || !formData.product_variant_id || formData.quantity === '') {
                setError('Store, vendor, product, variant, and quantity are required');
                return;
            }

            const inventoryData = {
                store_id: formData.store_id,
                product_variant_id: formData.product_variant_id,
                quantity: parseInt(formData.quantity),
                min_stock_level: parseInt(formData.min_stock_level) || 10,
                reorder_point: parseInt(formData.reorder_point) || 20
            };

            console.log('Creating inventory with data:', inventoryData);

            const response = await inventoryAPI.create(inventoryData);

            if (response.data && response.data.status === 'success') {
                setSuccessMessage(response.data.message || 'Inventory item created successfully!');
                setShowCreateModal(false);
                resetForm();
                fetchInventory();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to create inventory item');
            }

        } catch (err) {
            console.error('Error creating inventory item:', err);
            setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create inventory item');
        } finally {
            setActionLoading(false);
        }
    };

    // Update inventory item
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!selectedItem?.id) {
            setError('Inventory item ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            if (!formData.store_id || !formData.product_variant_id || formData.quantity === '') {
                setError('Store, product variant, and quantity are required');
                return;
            }

            const inventoryData = {
                quantity: parseInt(formData.quantity),
                min_stock_level: parseInt(formData.min_stock_level) || 10,
                reorder_point: parseInt(formData.reorder_point) || 20
            };

            // Only include store_id and product_variant_id if they have changed
            if (formData.store_id !== selectedItem.store_id) {
                inventoryData.store_id = formData.store_id;
            }

            if (formData.product_variant_id !== selectedItem.product_variant_id) {
                inventoryData.product_variant_id = formData.product_variant_id;
            }

            console.log(`Updating inventory item ${selectedItem.id} with data:`, inventoryData);

            const response = await inventoryAPI.update(selectedItem.id, inventoryData);

            console.log('Update response:', response.data);

            if (response.data && response.data.status === 'success') {
                setSuccessMessage(response.data.message || 'Inventory item updated successfully!');
                setShowEditModal(false);
                resetForm();
                setSelectedItem(null);
                await fetchInventory();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to update inventory item');
            }

        } catch (err) {
            console.error('Error updating inventory item:', err);
            let errorMsg = 'Failed to update inventory item';

            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                errorMsg = Object.values(errors).flat().join(', ');
            } else if (err.message) {
                errorMsg = err.message;
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    // Delete inventory item
    const handleDelete = async () => {
        if (!selectedItem?.id) {
            setError('Inventory item ID is missing');
            setShowDeleteModal(false);
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            console.log(`Deleting inventory item with ID: ${selectedItem.id}`);

            const response = await inventoryAPI.delete(selectedItem.id);

            console.log('Delete response:', response.data);

            if (response.data && response.data.status === 'success') {
                const deleteMessage = response.data.data?.message || response.data.message || 'Inventory item deleted successfully!';
                setSuccessMessage(deleteMessage);

                const deletedId = response.data.data?.data?.deleted_id || selectedItem.id;

                setInventory(prev => prev.filter(item => item.id !== deletedId));
                setFilteredInventory(prev => prev.filter(item => item.id !== deletedId));

                setPagination(prev => ({
                    ...prev,
                    total: Math.max(0, prev.total - 1)
                }));

                setShowDeleteModal(false);
                setSelectedItem(null);
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to delete inventory item');
            }

        } catch (err) {
            console.error('Error deleting inventory item:', err);

            let errorMsg = 'Failed to delete inventory item';
            if (err.response?.data?.message) {
                errorMsg = err.response.data.message;
            } else if (err.response?.data?.data?.message) {
                errorMsg = err.response.data.data.message;
            } else if (err.response?.data?.error) {
                errorMsg = err.response.data.error;
            } else if (err.message) {
                errorMsg = err.message;
            }

            setError(errorMsg);
        } finally {
            setActionLoading(false);
        }
    };

    // Adjust inventory quantity
    const handleAdjust = async (e) => {
        e.preventDefault();
        if (!selectedItem?.id) {
            setError('Inventory item ID is missing');
            return;
        }

        try {
            setActionLoading(true);
            setError('');
            setSuccessMessage('');

            const delta = parseInt(adjustData.quantity_delta);
            if (isNaN(delta)) {
                setError('Please enter a valid quantity change');
                return;
            }

            const adjustmentData = {
                quantity_delta: delta,
                reason: adjustData.reason || 'Manual adjustment',
                notes: adjustData.notes || ''
            };

            console.log(`Adjusting inventory item ${selectedItem.id} with delta: ${delta}`);

            const response = await inventoryAPI.adjust(selectedItem.id, adjustmentData);

            if (response.data && response.data.status === 'success') {
                setSuccessMessage(response.data.message || 'Inventory adjusted successfully!');
                setShowAdjustModal(false);
                setAdjustData({ quantity_delta: '', reason: '', notes: '' });
                setSelectedItem(null);
                fetchInventory();
                setTimeout(() => setSuccessMessage(''), 3000);
            } else {
                setError(response.data?.message || 'Failed to adjust inventory');
            }

        } catch (err) {
            console.error('Error adjusting inventory:', err);
            setError(err.response?.data?.message || 'Failed to adjust inventory');
        } finally {
            setActionLoading(false);
        }
    };

    // Quick adjust functions
    const quickAdjust = async (itemId, delta) => {
        if (!itemId) {
            setError('Inventory item ID is required');
            return;
        }

        try {
            const adjustmentData = {
                quantity_delta: delta,
                reason: 'Quick adjustment',
                notes: `Quick ${delta > 0 ? 'add' : 'remove'} of ${Math.abs(delta)} units`
            };

            console.log(`Quick adjusting inventory item ${itemId} with delta: ${delta}`);

            await inventoryAPI.adjust(itemId, adjustmentData);
            fetchInventory();
        } catch (err) {
            console.error('Error in quick adjust:', err);
            setError('Failed to adjust inventory');
        }
    };

    // Open edit modal
    const openEditModal = (item) => {
        console.log('Opening edit modal for item:', item);
        setSelectedItem(item);

        // Find vendor and product from the item
        const variant = variants.find(v => v.id === item.product_variant_id);
        const product = products.find(p => p.id === variant?.product_id);

        setFormData({
            store_id: item.store_id || '',
            vendor_id: product?.vendor_id || item.vendor_id || '',
            product_id: product?.id || item.product_id || '',
            product_variant_id: item.product_variant_id || '',
            quantity: item.quantity?.toString() || '',
            min_stock_level: item.min_stock_level?.toString() || '10',
            reorder_point: item.reorder_point?.toString() || '20'
        });

        setShowEditModal(true);
        setError('');
    };

    // Open delete modal
    const openDeleteModal = (item) => {
        console.log('Opening delete modal for item:', item);
        setSelectedItem(item);
        setShowDeleteModal(true);
        setError('');
    };

    // Open adjust modal
    const openAdjustModal = (item) => {
        console.log('Opening adjust modal for item:', item);
        setSelectedItem(item);
        setAdjustData({ quantity_delta: '', reason: '', notes: '' });
        setShowAdjustModal(true);
        setError('');
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            store_id: '',
            vendor_id: '',
            product_id: '',
            product_variant_id: '',
            quantity: '',
            min_stock_level: '10',
            reorder_point: '20'
        });
        setSelectedItem(null);
        setError('');
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        resetForm();
    };

    // Pagination handlers
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.pages) {
            setPagination(prev => ({ ...prev, page }));
        }
    };

    // Get stock status
    const getStockStatus = (quantity, reorderPoint, minStock) => {
        if (quantity <= 0) return { variant: 'danger', label: 'Out of Stock', icon: <FaExclamationTriangle /> };
        if (quantity <= minStock) return { variant: 'danger', label: 'Critical', icon: <FaExclamationTriangle /> };
        if (quantity <= reorderPoint) return { variant: 'warning', label: 'Low Stock', icon: <FaExclamationTriangle /> };
        return { variant: 'success', label: 'In Stock', icon: <FaCheckCircle /> };
    };

    // Get store name
    const getStoreName = (storeId) => {
        if (!storeId) return 'Unknown Store';
        const store = stores.find(s => s.id === storeId);
        return store ? store.name : `Store ${storeId.substring(0, 8)}...`;
    };

    // Get vendor name
    const getVendorName = (vendorId) => {
        if (!vendorId) return 'Unknown Vendor';
        const vendor = vendors.find(v => v.id === vendorId);
        return vendor ? vendor.name : `Vendor ${vendorId.substring(0, 8)}...`;
    };

    // Get product name
    const getProductName = (productId) => {
        if (!productId) return 'Unknown Product';
        const product = products.find(p => p.id === productId);
        return product ? product.name : `Product ${productId.substring(0, 8)}...`;
    };

    // Get variant name
    const getVariantName = (variantId) => {
        if (!variantId) return 'Unknown Variant';
        const variant = variants.find(v => v.id === variantId);
        return variant ? (variant.title || variant.name) : `Variant ${variantId.substring(0, 8)}...`;
    };

    // Get variant SKU
    const getVariantSKU = (variantId) => {
        if (!variantId) return 'N/A';
        const variant = variants.find(v => v.id === variantId);
        return variant?.sku || 'N/A';
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    // Render pagination
    const renderPagination = () => {
        if (pagination.pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, pagination.page - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pagination.pages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        items.push(
            <Pagination.Prev
                key="prev"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
            />
        );

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} active={1 === pagination.page} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === pagination.page}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < pagination.pages) {
            if (endPage < pagination.pages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
            }
            items.push(
                <Pagination.Item
                    key={pagination.pages}
                    active={pagination.pages === pagination.page}
                    onClick={() => handlePageChange(pagination.pages)}
                >
                    {pagination.pages}
                </Pagination.Item>
            );
        }

        items.push(
            <Pagination.Next
                key="next"
                disabled={pagination.page === pagination.pages}
                onClick={() => handlePageChange(pagination.page + 1)}
            />
        );

        return items;
    };

    // Calculate statistics
    const calculateStats = () => {
        const totalItems = inventory.length;
        const totalStock = inventory.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const outOfStock = inventory.filter(item => (item.quantity || 0) <= 0).length;
        const lowStock = inventory.filter(item => {
            const quantity = item.quantity || 0;
            const minStock = item.min_stock_level || 10;
            return quantity > 0 && quantity <= minStock;
        }).length;
        const criticalStock = inventory.filter(item => {
            const quantity = item.quantity || 0;
            const minStock = item.min_stock_level || 10;
            return quantity > 0 && quantity <= (minStock * 0.5);
        }).length;
        const uniqueStores = new Set(inventory.map(item => item.store_id)).size;
        const uniqueVendors = new Set(inventory.map(item => item.vendor_id)).size;
        const uniqueProducts = new Set(inventory.map(item => item.product_id)).size;

        return {
            totalItems,
            totalStock,
            outOfStock,
            lowStock,
            criticalStock,
            uniqueStores,
            uniqueVendors,
            uniqueProducts
        };
    };

    const stats = calculateStats();

    if (loading || storesLoading) return (
        <div className="text-center py-5">
            <Spinner animation="border" variant="success" role="status">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2 text-muted">Loading inventory...</p>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Inventory Management</h1>
                    <p className="text-muted mb-0">Manage stock levels across all stores</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Add Stock
                    </Button>
                </Col>
            </Row>

            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <strong>Error:</strong> {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    <strong>Success:</strong> {successMessage}
                </Alert>
            )}

            {/* Stats Summary */}
            <Row className="mb-4 g-3">
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-primary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.totalItems}</div>
                            <small>Total Items</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-success text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.totalStock}</div>
                            <small>Total Stock</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-info text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.uniqueStores}</div>
                            <small>Stores</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-warning text-dark">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.uniqueVendors}</div>
                            <small>Vendors</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-danger text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.outOfStock}</div>
                            <small>Out of Stock</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm bg-secondary text-white">
                        <Card.Body className="text-center py-3">
                            <div className="h4 mb-1">{stats.uniqueProducts}</div>
                            <small>Products</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search inventory by product, store, vendor, or SKU..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1 txt-cl">Filter by Store</Form.Label>
                                <Form.Select
                                    value={selectedStore}
                                    onChange={(e) => setSelectedStore(e.target.value)}
                                    size="sm"
                                >
                                    <option value="">All Stores</option>
                                    {stores.map(store => (
                                        <option key={store.id} value={store.id}>
                                            {store.name} {store.code ? `(${store.code})` : ''}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={5} className="text-md-end d-flex align-items-center justify-content-end">
                            <div className="me-4">
                                <small className="text-muted">
                                    Total: <strong>{pagination.total}</strong> •
                                    Showing: <strong>{searchTerm ? filteredInventory.length : inventory.length}</strong> items
                                </small>
                            </div>
                            {(searchTerm || selectedStore || selectedVariant) && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedStore('');
                                        setSelectedVariant('');
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Inventory Table */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th width="60"></th>
                                <th>
                                    <div className="d-flex align-items-center">
                                        Product/Variant
                                        <Button
                                            variant="link"
                                            className="p-0 ms-1"
                                            onClick={() => handleSort('product_variant_name')}
                                        >
                                            {sortConfig.key === 'product_variant_name' ? (
                                                sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                                            ) : (
                                                <FaSort />
                                            )}
                                        </Button>
                                    </div>
                                </th>
                                <th>
                                    <div className="d-flex align-items-center">
                                        Vendor
                                        <Button
                                            variant="link"
                                            className="p-0 ms-1"
                                            onClick={() => handleSort('vendor_name')}
                                        >
                                            {sortConfig.key === 'vendor_name' ? (
                                                sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                                            ) : (
                                                <FaSort />
                                            )}
                                        </Button>
                                    </div>
                                </th>
                                <th>
                                    <div className="d-flex align-items-center">
                                        Store
                                        <Button
                                            variant="link"
                                            className="p-0 ms-1"
                                            onClick={() => handleSort('store_name')}
                                        >
                                            {sortConfig.key === 'store_name' ? (
                                                sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                                            ) : (
                                                <FaSort />
                                            )}
                                        </Button>
                                    </div>
                                </th>
                                <th>
                                    <div className="d-flex align-items-center">
                                        Stock
                                        <Button
                                            variant="link"
                                            className="p-0 ms-1"
                                            onClick={() => handleSort('quantity')}
                                        >
                                            {sortConfig.key === 'quantity' ? (
                                                sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                                            ) : (
                                                <FaSort />
                                            )}
                                        </Button>
                                    </div>
                                </th>
                                <th>Status</th>
                                <th>Last Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInventory.length > 0 ? (
                                filteredInventory.map(item => {
                                    const status = getStockStatus(
                                        item.quantity || 0,
                                        item.reorder_point || 20,
                                        item.min_stock_level || 10
                                    );

                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <div
                                                    className="bg-info rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <FaBoxes className="text-white" size={16} />
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">{getVariantName(item.product_variant_id)}</div>
                                                    <small className="text-muted">
                                                        SKU: {getVariantSKU(item.product_variant_id)} •
                                                        Product: {item.product_name || 'N/A'}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">
                                                        <FaUser className="me-1 text-muted" size={12} />
                                                        {item.vendor_name || getVendorName(item.vendor_id)}
                                                    </div>
                                                    <small className="text-muted">
                                                        ID: {item.vendor_id?.substring(0, 12)}...
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <div className="fw-semibold">
                                                        <FaStore className="me-1 text-muted" size={12} />
                                                        {getStoreName(item.store_id)}
                                                    </div>
                                                    <small className="text-muted">
                                                        Store ID: {item.store_id?.substring(0, 12)}...
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <span className={`fw-bold text-${status.variant}`}>
                                                        {item.quantity || 0}
                                                    </span>
                                                    <div className="ms-2 d-flex gap-1">
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            className="p-1"
                                                            onClick={() => quickAdjust(item.id, 1)}
                                                            title="Add 1"
                                                            disabled={actionLoading}
                                                        >
                                                            <FaPlusSmall size={10} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="p-1"
                                                            onClick={() => quickAdjust(item.id, -1)}
                                                            title="Remove 1"
                                                            disabled={actionLoading}
                                                        >
                                                            <FaMinus size={10} />
                                                        </Button>
                                                    </div>
                                                </div>
                                                <div className="small text-muted mt-1">
                                                    Min: {item.min_stock_level || 10} •
                                                    Reorder: {item.reorder_point || 20}
                                                </div>
                                            </td>
                                            <td>
                                                <Badge bg={status.variant} className="d-flex align-items-center gap-1">
                                                    {status.icon}
                                                    {status.label}
                                                </Badge>
                                            </td>
                                            <td>
                                                <div className="small text-muted">
                                                    {formatDate(item.updated_at)}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openEditModal(item)}
                                                        title="Edit Inventory"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaEdit className="me-1" />
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="outline-warning"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openAdjustModal(item)}
                                                        title="Adjust Stock"
                                                        disabled={actionLoading}
                                                    >
                                                        Adjust
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => openDeleteModal(item)}
                                                        title="Delete Inventory"
                                                        disabled={actionLoading}
                                                    >
                                                        <FaTrash className="me-1" />
                                                        Delete
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="8" className="text-center py-5">
                                        <div className="text-muted">
                                            {searchTerm || selectedStore || selectedVariant ? (
                                                <>
                                                    <FaSearch className="h3 mb-3" />
                                                    <p>No inventory items found matching your filters</p>
                                                </>
                                            ) : (
                                                <>
                                                    <FaBoxes className="h3 mb-3" />
                                                    <p>No inventory items found</p>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <small className="text-muted">
                                    Page {pagination.page} of {pagination.pages} •
                                    Showing {inventory.length} of {pagination.total} items
                                </small>
                            </div>
                            <Pagination className="mb-0">
                                {renderPagination()}
                            </Pagination>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={handleModalClose}
                title="Add New Inventory Item"
                onSubmit={handleCreate}
                submitText={actionLoading ? "Creating..." : "Add Inventory"}
                size="lg"
                loading={actionLoading}
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store *</Form.Label>
                            <Form.Select
                                value={formData.store_id}
                                onChange={(e) => handleStoreChange(e.target.value)}
                                required
                                disabled={actionLoading}
                            >
                                <option value="">Select a store</option>
                                {stores.map(store => (
                                    <option key={store.id} value={store.id}>
                                        {store.name} {store.code ? `(${store.code})` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                            {stores.length === 0 && !storesLoading && (
                                <Form.Text className="text-danger">
                                    No stores available. Please create a store first.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Vendor *</Form.Label>
                            <Form.Select
                                value={formData.vendor_id}
                                onChange={(e) => handleVendorChange(e.target.value)}
                                required
                                disabled={actionLoading || !formData.store_id}
                            >
                                <option value="">Select a vendor</option>
                                {formData.store_id ? (
                                    filteredVendors.length > 0 ? (
                                        filteredVendors.map(vendor => (
                                            <option key={vendor.id} value={vendor.id}>
                                                {vendor.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled value="">No vendors for this store</option>
                                    )
                                ) : (
                                    <option disabled value="">Please select a store first</option>
                                )}
                            </Form.Select>
                            {formData.store_id && filteredVendors.length === 0 && (
                                <Form.Text className="text-warning">
                                    No vendors associated with this store. Please assign vendors first.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product *</Form.Label>
                            <Form.Select
                                value={formData.product_id}
                                onChange={(e) => handleProductChange(e.target.value)}
                                required
                                disabled={actionLoading || !formData.vendor_id}
                            >
                                <option value="">Select a product</option>
                                {formData.vendor_id ? (
                                    filteredProducts.length > 0 ? (
                                        filteredProducts.map(product => (
                                            <option key={product.id} value={product.id}>
                                                {product.name} {product.sku ? `(SKU: ${product.sku})` : ''}
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled value="">No products for this vendor</option>
                                    )
                                ) : (
                                    <option disabled value="">Please select a vendor first</option>
                                )}
                            </Form.Select>
                            {formData.vendor_id && filteredProducts.length === 0 && (
                                <Form.Text className="text-warning">
                                    No products for this vendor. Please create products first.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Variant *</Form.Label>
                            <Form.Select
                                value={formData.product_variant_id}
                                onChange={(e) => setFormData({ ...formData, product_variant_id: e.target.value })}
                                required
                                disabled={actionLoading || !formData.product_id}
                            >
                                <option value="">Select a variant</option>
                                {formData.product_id ? (
                                    filteredVariants.length > 0 ? (
                                        filteredVariants.map(variant => (
                                            <option key={variant.id} value={variant.id}>
                                                {variant.title || variant.name} (SKU: {variant.sku})
                                            </option>
                                        ))
                                    ) : (
                                        <option disabled value="">No variants for this product</option>
                                    )
                                ) : (
                                    <option disabled value="">Please select a product first</option>
                                )}
                            </Form.Select>
                            {formData.product_id && filteredVariants.length === 0 && (
                                <Form.Text className="text-warning">
                                    No variants for this product. Please create variants first.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quantity *</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.quantity}
                                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                required
                                placeholder="Enter quantity"
                                min="0"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Minimum Stock Level</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.min_stock_level}
                                onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                                placeholder="Minimum stock level"
                                min="0"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Stock level considered critical
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Reorder Point</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.reorder_point}
                                onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                                placeholder="Reorder point"
                                min="0"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Stock level to trigger reorder
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Edit Modal */}
            <FormModal
                show={showEditModal}
                onHide={() => !actionLoading && setShowEditModal(false)}
                title={`Edit Inventory Item - ${selectedItem?.product_variant_name || selectedItem?.variant_title || ''}`}
                onSubmit={handleUpdate}
                submitText={actionLoading ? "Updating..." : "Update Inventory"}
                size="lg"
                loading={actionLoading}
            >
                {selectedItem && (
                    <Alert variant="info" className="small">
                        <strong>Inventory ID:</strong> {selectedItem.id}
                    </Alert>
                )}

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Store *</Form.Label>
                            <Form.Select
                                value={formData.store_id}
                                onChange={(e) => handleStoreChange(e.target.value)}
                                required
                                disabled={actionLoading}
                            >
                                <option value="">Select a store</option>
                                {stores.map(store => (
                                    <option key={store.id} value={store.id}>
                                        {store.name} {store.code ? `(${store.code})` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Product Variant *</Form.Label>
                            <Form.Select
                                value={formData.product_variant_id}
                                onChange={(e) => setFormData({ ...formData, product_variant_id: e.target.value })}
                                required
                                disabled={actionLoading || !formData.store_id}
                            >
                                <option value="">Select a product variant</option>
                                {formData.store_id ? (
                                    variants.map(variant => (
                                        <option key={variant.id} value={variant.id}>
                                            {variant.title || variant.name} (SKU: {variant.sku})
                                        </option>
                                    ))
                                ) : (
                                    <option disabled>Please select a store first</option>
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Quantity *</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.quantity}
                                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                                required
                                placeholder="Enter quantity"
                                min="0"
                                disabled={actionLoading}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Minimum Stock Level</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.min_stock_level}
                                onChange={(e) => setFormData({ ...formData, min_stock_level: e.target.value })}
                                placeholder="Minimum stock level"
                                min="0"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Stock level considered critical
                            </Form.Text>
                        </Form.Group>
                    </Col>
                    <Col md={4}>
                        <Form.Group className="mb-3">
                            <Form.Label>Reorder Point</Form.Label>
                            <Form.Control
                                type="number"
                                value={formData.reorder_point}
                                onChange={(e) => setFormData({ ...formData, reorder_point: e.target.value })}
                                placeholder="Reorder point"
                                min="0"
                                disabled={actionLoading}
                            />
                            <Form.Text className="text-muted">
                                Stock level to trigger reorder
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>

                {selectedItem && (
                    <div className="alert alert-info small mt-3">
                        <strong>Current Status:</strong> {getStockStatus(
                            selectedItem.quantity || 0,
                            selectedItem.reorder_point || 20,
                            selectedItem.min_stock_level || 10
                        ).label}
                    </div>
                )}
            </FormModal>

            {/* Adjust Modal */}
            <FormModal
                show={showAdjustModal}
                onHide={() => !actionLoading && setShowAdjustModal(false)}
                title={`Adjust Inventory - ${selectedItem?.product_variant_name || selectedItem?.variant_title || ''}`}
                onSubmit={handleAdjust}
                submitText={actionLoading ? "Adjusting..." : "Adjust Stock"}
                size="lg"
                loading={actionLoading}
            >
                {selectedItem && (
                    <div className="mb-4">
                        <Alert variant="light" className="mb-3">
                            <strong>Inventory ID:</strong> {selectedItem.id}
                        </Alert>
                        <Row>
                            <Col md={6}>
                                <div className="alert alert-light">
                                    <small className="text-muted">Product Variant</small>
                                    <div className="fw-semibold">{selectedItem.product_variant_name || selectedItem.variant_title}</div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="alert alert-light">
                                    <small className="text-muted">Current Stock</small>
                                    <div className="fw-semibold">{selectedItem.quantity || 0} units</div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Quantity Adjustment *</Form.Label>
                    <InputGroup>
                        <Form.Control
                            type="number"
                            value={adjustData.quantity_delta}
                            onChange={(e) => setAdjustData({ ...adjustData, quantity_delta: e.target.value })}
                            required
                            placeholder="Enter quantity change"
                            disabled={actionLoading}
                        />
                        <InputGroup.Text>
                            {parseInt(adjustData.quantity_delta || 0) > 0 ? 'Add' : 'Remove'}
                        </InputGroup.Text>
                    </InputGroup>
                    <Form.Text className="text-muted">
                        Enter positive number to add stock, negative number to remove stock
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Reason for Adjustment *</Form.Label>
                    <Form.Select
                        value={adjustData.reason}
                        onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                        required
                        disabled={actionLoading}
                    >
                        <option value="">Select a reason</option>
                        <option value="restock">Restock/Replenishment</option>
                        <option value="sale">Sale/Order Fulfillment</option>
                        <option value="damage">Damage/Return</option>
                        <option value="transfer">Store Transfer</option>
                        <option value="correction">Data Correction</option>
                        <option value="other">Other</option>
                    </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={adjustData.notes}
                        onChange={(e) => setAdjustData({ ...adjustData, notes: e.target.value })}
                        placeholder="Additional notes about this adjustment"
                        disabled={actionLoading}
                    />
                </Form.Group>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => !actionLoading && setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Inventory Item"
                message={
                    <div>
                        <Alert variant="warning">
                            <strong>Warning:</strong> This action will permanently delete this inventory record.
                        </Alert>
                        <div className="alert alert-light">
                            <strong>Inventory ID:</strong> {selectedItem?.id}<br />
                            <strong>Product:</strong> {selectedItem?.product_variant_name || selectedItem?.variant_title}<br />
                            <strong>Vendor:</strong> {selectedItem?.vendor_name}<br />
                            <strong>Store:</strong> {selectedItem?.store_name}<br />
                            <strong>Current Stock:</strong> {selectedItem?.quantity} units
                        </div>
                        <p className="text-danger mb-0 mt-2">
                            <strong>This action cannot be undone.</strong> All stock data will be permanently deleted.
                        </p>
                    </div>
                }
                confirmText={actionLoading ? "Deleting..." : "Delete"}
                confirmDisabled={actionLoading}
                variant="danger"
                loading={actionLoading}
            />
        </div>
    );
};

export default Inventory;